import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Button from '@/components/atoms/button';
import ReviewOrderSummaryItem from './order-summary-item';
import { Money } from '@shared/types/product';
import { LineItem } from '@shared/types/cart';
import EditIcon from '../sections/EditIcon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { desktop } from '@/constants/screensizes';
import { Currency } from '@/types/currency';
import useFormat from '@/hooks/useFormat';
import { sdk } from '@/sdk/CommercetoolsSDK';
import toast from '@/components/atoms/toaster/helpers/toast';
import Spinner from '@/components/organisms/spinner';
import useAccount from '@/lib/hooks/useAccount';
import Modal from '@/components/organisms/modal';
import Image from "@/components/atoms/Image";
import Typography from '@/components/atoms/typography';
import Popup from '@/components/atoms/project-builder/popup';
import Icon from '@/components/icons/x-mark';
import Service from '@/service';
import { getCurrencyByLocale } from '@/utils/currency-helpers';
import getCookieValue from '@/utils/cookie';


const ReviewOrderSummary = ({ subTotal, total, gst, lineItems, vouchers, handleReview, FirstName, LastName, Address, Postcode, Country, State, Suburb, Phone, City, cartId, ctOrderNumber }: any) => {
    const [isDesktop] = useMediaQuery(desktop);
    const [isLoading, setIsLoading] = useState(false);
    const { account } = useAccount();
    const searchParams = useSearchParams();
    const router = useRouter();
    const headerFontCss = isDesktop ? 'text-28' : 'text-20';
    const extensions = sdk.composableCommerce;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const closeModal = () => {
        setIsModalOpen(false);
    };
    const [enableTryAgain, setEnableTryAgain] = useState(true);
    const locale = getCookieValue('locale');
    const currencyCode = getCurrencyByLocale(locale);

    // Track in-progress verifications to prevent double execution
    const ewayVerificationInProgress = useRef<string | null>(null);
    const windcaveVerificationInProgress = useRef<string | null>(null);

    const totalProductsCount = lineItems.reduce((acc: number, curr: LineItem) => {
        acc += (curr.count || 0);
        return acc;
    }, 0);

    // Handle payment callback when returning from eWay
    useEffect(() => {
        const accessCode = searchParams.get('AccessCode');

        if (!accessCode) {
            return;
        }

        // Validate cart ID - prevent processing if cart ID doesn't match
        const storedCartId = sessionStorage.getItem('payment_cart_id');

        if (!storedCartId) {
            console.error('Payment callback security check failed: No cart ID found');
            setErrorMessage('Invalid payment session. Please initiate payment from the cart.');
            setIsModalOpen(true);
            return;
        }

        if (cartId !== storedCartId) {
            console.error('Payment callback security check failed: Cart ID mismatch');
            console.error(`Expected: ${storedCartId}, Got: ${cartId}`);
            setErrorMessage('Invalid payment session. Please initiate payment from the cart.');
            setIsModalOpen(true);
            return;
        }

        // Check sessionStorage for completed verifications (persists across page reloads)
        const storageKey = `payment_verified_${accessCode}`;
        const alreadyProcessed = sessionStorage.getItem(storageKey);

        if (alreadyProcessed) {
            console.log('Payment already processed for this AccessCode (from storage), skipping...');
            return;
        }

        // Check ref to prevent double execution during same session (React Strict Mode)
        if (ewayVerificationInProgress.current === accessCode) {
            console.log('eWay verification already in progress for this AccessCode, skipping...');
            return;
        }

        // Mark as in-progress
        ewayVerificationInProgress.current = accessCode;
        setIsLoading(true);

        const verifyPaymentCallback = async () => {
            try {
                console.log('Verifying payment with AccessCode:', accessCode);
                const result = await extensions.payment.verifyPayment({
                    accessCode,
                });
                console.log('Payment verification result:', result.statusCode);
                const transaction = result?.data?.data?.attributes?.Transactions?.[0];

                if (transaction?.TransactionStatus === true && transaction?.TransactionID !== null) {
                    sessionStorage.setItem(storageKey, 'true');
                    handleReview(transaction?.TransactionID);
                } else if (transaction?.TransactionStatus === false) {
                    setIsLoading(false);
                    ewayVerificationInProgress.current = null;
                } else if (result.statusCode === 500) {
                    setIsLoading(false);
                    ewayVerificationInProgress.current = null;
                    setErrorMessage(`Something went wrong on the payment server, our team is here to help`);
                    setIsModalOpen(true);
                    setEnableTryAgain(false);
                } else {
                    setIsLoading(false);
                    ewayVerificationInProgress.current = null;
                }
            } catch (error: any) {
                console.error('Payment verification error:', error);
                setIsLoading(false);
                ewayVerificationInProgress.current = null;
            }
        };

        verifyPaymentCallback();
    }, [searchParams]);

    useEffect(() => {
        const sessionId = searchParams.get('sessionId');
        const paymentStatus = searchParams.get('paymentStatus');

        if (!sessionId) {
            return;
        }

        // Check if payment was cancelled - don't verify in this case
        if (paymentStatus === 'cancelled') {
            console.log('Payment was cancelled by user, skipping verification');
            setIsLoading(false);
            return;
        }

        // Validate cart ID - prevent processing if cart ID doesn't match
        const storedCartId = sessionStorage.getItem('payment_cart_id');

        if (!storedCartId) {
            console.error('Payment callback security check failed: No cart ID found');
            setErrorMessage('Invalid payment session. Please initiate payment from the cart.');
            setIsModalOpen(true);
            return;
        }

        if (cartId !== storedCartId) {
            console.error('Payment callback security check failed: Cart ID mismatch');
            console.error(`Expected: ${storedCartId}, Got: ${cartId}`);
            setErrorMessage('Invalid payment session. Please initiate payment from the cart.');
            setIsModalOpen(true);
            return;
        }

        // Check sessionStorage for completed verifications (persists across page reloads)
        const storageKey = `payment_verified_${sessionId}`;
        const alreadyProcessed = sessionStorage.getItem(storageKey);

        if (alreadyProcessed) {
            console.log('Payment already processed for this sessionId (from storage), skipping...');
            return;
        }

        // Check ref to prevent double execution during same session (React Strict Mode)
        if (windcaveVerificationInProgress.current === sessionId) {
            console.log('Windcave verification already in progress for this sessionId, skipping...');
            return;
        }

        // Mark as in-progress
        windcaveVerificationInProgress.current = sessionId;
        setIsLoading(true);

        const verifyWindcavePayment = async (sessionId: string) => {
            try {
                const result = await extensions.payment.windcaveVerifySession({
                    sessionId,
                });
                console.log("result", result);
                const status = result?.data?.data?.transactions[0]?.responseText;
                if (status === 'APPROVED' && result?.data?.data?.transactions[0]?.id !== null) {
                    sessionStorage.setItem(storageKey, 'true');
                    const transactionId = result?.data?.data?.transactions[0]?.id;
                    handleReview(transactionId);
                } else {
                    setIsLoading(false);
                    windcaveVerificationInProgress.current = null; // Reset on failure
                    console.log("its coming here");

                    // Get detailed decline reason
                    const declineInfo = status?.declineReason;
                    let errorMsg = 'Something went wrong on the payment server, our team is here to help.';

                    if (declineInfo) {
                        errorMsg = `${declineInfo.message}: ${declineInfo.details}`;
                    } else {
                        const responseText = status?.responseText;
                        if (responseText) {
                            errorMsg = `Payment declined: ${responseText}`;
                        }
                    }
                    setErrorMessage(errorMsg);
                    setIsModalOpen(true);
                    setEnableTryAgain(false);
                }
            } catch (error) {
                setIsLoading(false);
                windcaveVerificationInProgress.current = null;
                setErrorMessage('Something went wrong on the payment server, our team is here to help.');
                setIsModalOpen(true);
                setEnableTryAgain(false);
            }
        };

        verifyWindcavePayment(sessionId);
    }, [searchParams]);

    const handlePayNow = async () => {
        // Get the current URL for redirect after payment
        const currentUrl = window.location.origin;
        const redirectUrl = `${currentUrl}/checkout/review`;

        // Store cartId in sessionStorage for validation on return
        sessionStorage.setItem('payment_cart_id', cartId);

        // Use sessionStorage value to guarantee the same ctOrderNumber that fetchCheckoutDetails
        // returned is sent as InvoiceReference, consistent with what create-order receives.
        //const resolvedCtOrderNumber = ctOrderNumber || sessionStorage.getItem('checkout_ctOrderNumber') || '';
        const resolvedCtOrderNumber = sessionStorage.getItem('checkout_ctOrderNumber') || '';
        console.log('[handlePayNow] ctOrderNumber being sent to eWay InvoiceReference:', resolvedCtOrderNumber);

        console.log("Cart ID:", cartId);

        const requestData = {
            totalAmount: total,
            gstAmount: gst,
            subTotalAmount: subTotal,
            redirectUrl: redirectUrl,
            customerReference: '',
            invoiceNumber: '',
            invoiceDescription: 'Plumbers Hub Order Payment',
            cartId: cartId,
            ctOrderNumber: resolvedCtOrderNumber,
            // Customer details to hide the form on eWAY payment page
            customerData: {
                firstName: FirstName || '',
                lastName: LastName || '',
                email: account?.email || '',
                phone: Phone || '',
                street1: Address || '',
                city: City || '',
                suburb: '',
                state: State || '',
                postalCode: Postcode || '',
                country: Country || ''
            }
        };

        try {
            setIsLoading(true);
            const responseBody = await extensions.payment.payNow(JSON.stringify(requestData));
            // The actual eWay data is nested at responseBody.data.data
            const ewayData = responseBody?.data?.data;

            // Check if we received a payment URL from eWay
            if (ewayData?.attributes?.SharedPaymentUrl) {
                window.location.href = ewayData.attributes.SharedPaymentUrl;
            } else if (ewayData?.attributes?.FormActionURL && ewayData?.attributes?.AccessCode) {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = ewayData.attributes.FormActionURL;

                const accessCodeInput = document.createElement('input');
                accessCodeInput.type = 'hidden';
                accessCodeInput.name = 'AccessCode';
                accessCodeInput.value = ewayData.attributes.AccessCode;

                form.appendChild(accessCodeInput);
                document.body.appendChild(form);
                form.submit();
            } else {
                console.error('No payment URL received from eWay. Full response:', responseBody);
                setErrorMessage('Your payment didn’t go through. Please try again, and if the issue persists, our team is here to help.');
                setIsModalOpen(true);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Pay Now Response Error:', error);
            setIsLoading(false);
        }
    };

    const gstCentAmount = gst?.centAmount || 0;
    const gstString = gstCentAmount ? `$${(gstCentAmount / 100).toLocaleString()}` : '$0.00';
    const gstAmountString = (gst?.displayAmount) ? gst?.displayAmount : '$0.00';
    const { formatCurrency } = useFormat();

    const handleWindcavePayment = async () => {
        const currentUrl = window.location.origin;
        const redirectUrl = `${currentUrl}/checkout/review`;

        // Store cartId and gateway type
        sessionStorage.setItem('payment_cart_id', cartId);
        sessionStorage.setItem('payment_gateway', 'windcave');

        const requestData = {
            totalAmount: total,
            gstAmount: gst,
            subTotalAmount: subTotal,
            redirectUrl: redirectUrl,
            invoiceNumber: '',
            invoiceDescription: 'Plumbers Hub Order Payment',
            cartId: cartId,
            customerData: {
                firstName: FirstName || '',
                lastName: LastName || '',
                email: account?.email || '',
                phone: Phone || '',
                street1: Address || '',
                city: City || '',
                suburb: '',
                state: State || '',
                postalCode: Postcode || '',
                country: Country || ''
            }
        };

        try {
            setIsLoading(true);
            const responseBody = await extensions.payment.windcaveCreateSession(JSON.stringify(requestData));
            const windcaveData = responseBody?.data?.data;
            if (windcaveData?.paymentUrl) {
                // Redirect to Windcave hosted payment form
                window.location.href = windcaveData.paymentUrl;
            } else {
                console.error('No payment URL received from eWay. Full response:', responseBody);
                setErrorMessage('Your payment didn’t go through. Please try again, and if the issue persists, our team is here to help.');
                setIsModalOpen(true);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Windcave Payment Error:', error);
            setIsLoading(false);
        }
    };
    const [isNavigating, setIsNavigating] = useState(false);

    return (
        <>
            {(isLoading || isNavigating) && <Spinner />}
            {(isLoading || isNavigating) && <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>}
            <div className=' bg-[#FFFFFF]  md:bg-[#F3EDE9] p-4 2xl:mx-20 lg:mx-4 mx-1 lg:mt-12 mt-1'>
                <div className='flex justify-between items-center m-1'>
                    <div>
                        <h3 className={`${headerFontCss} font-semibold`}>4. Order Summary</h3>
                        {isDesktop && <p className='text-18 font-light mt-1'>{`${totalProductsCount} ${totalProductsCount > 1 ? 'items' : 'item'} in total`}</p>}
                    </div>
                    <EditIcon
                        location="/cart"
                        onClick={() => setIsNavigating(true)}
                    />
                </div>
                <div>
                    {lineItems.map((lineItem: any) => {
                        return <ReviewOrderSummaryItem key={lineItem.lineItemId} lineItem={lineItem} />
                    })}
                </div>
                <div className='md:flex md:flex-col md:gap-8'>
                    <div className='flex justify-end w-full'>
                        <div className='mt-5 w-full'>
                            <div className='mx-4 flex flex-col items-end'>
                                {/* Discount */}
                                {(vouchers ?? []).filter((v: any) => v?.discount?.amount > 0).map((v: any, i: number) => (
                                    <div key={i} className='grid grid-cols-2 gap-[24px] lg:gap-[60px] w-[280px] sm:w-[350px]'>
                                        <p className='font-medium mt-1 text-[15.5px] md:text-18 text-end text-[#E32C53]'>Discount</p>
                                        {/* <p className='font-semibold text-20 md:text-24 text-end whitespace-nowrap text-[#E32C53]'>-{v.discount.displayAmount}</p> */}
                                        {v.freeItemsCount > 0 ? (
                                            <p className="font-semibold text-20 md:text-24 text-end whitespace-nowrap text-[#E32C53]">
                                                 +{v.freeItemsCount} free
                                            </p>
                                        ) : (
                                            <p className="font-semibold text-20 md:text-24 text-end whitespace-nowrap text-[#E32C53]">
                                                 -{v.discount.displayAmount}
                                            </p>
                                        )}
                                    </div>
                                ))}
                                
                                {/* Sub Total */}
                                <div className='grid grid-cols-2 gap-[24px] lg:gap-[60px] w-[280px] sm:w-[350px]'>
                                    <p className='font-medium mt-1 text-[15.5px] md:text-18 text-end'>Sub Total</p>
                                    <p className='font-semibold text-20 md:text-24 text-end whitespace-nowrap'>
                                        {subTotal?.currencyCode ? formatCurrency((subTotal?.centAmount as number / 100), subTotal?.currencyCode as Currency) : '$0.00'}
                                    </p>
                                </div>

                                {/* GST */}
                                <div className='grid grid-cols-2 gap-[24px] lg:gap-[60px] w-[280px] sm:w-[350px] mt-2'>
                                    <p className='font-medium mt-1 text-[15.5px] md:text-18 text-end'>GST</p>
                                    <p className='font-semibold text-20 md:text-24 text-end whitespace-nowrap'>{gstAmountString}</p>
                                </div>

                                {/* Delivery Fee */}
                                <div className='grid grid-cols-2 gap-[24px] lg:gap-[60px] w-[280px] sm:w-[350px] mt-2'>
                                    <p className='font-medium mt-1 text-[15.5px] md:text-18 text-end'>Delivery Fee</p>
                                    <p className='font-semibold text-20 md:text-24 text-end whitespace-nowrap'>$0.00</p>
                                </div>

                                {/* Border separator before Total */}
                                <div className='w-[280px] sm:w-[350px] border-b border-[#D3D3D3] pb-2 mt-2' />
                            </div>

                            {/* Divider */}


                            {/* Total */}
                            <div className='my-3 mx-4'>
                                <div className='flex flex-col items-end'>
                                    <div className='grid grid-cols-2 gap-[24px] lg:gap-[60px] items-center w-[280px] sm:w-[350px]'>
                                        <p className='font-bold lg:font-medium text-17 md:text-22 text-end'>Total</p>
                                        <p className='font-semibold text-20 md:text-28 text-end whitespace-nowrap'>
                                            {total?.currencyCode ? formatCurrency((total?.centAmount as number / 100), total?.currencyCode as Currency) : '$0.00'}
                                        </p>
                                    </div>
                                    {locale === 'au' ? (
                                        <p className='text-12 md:text-18 font-light mt-1'>Price includes GST & GST Free Products</p>
                                    ) : (
                                        <p className='text-12 md:text-18 font-light mt-1'>Price includes GST</p>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                    {lineItems.some((item: LineItem) => item.productId === 'PHYPF011-AU') && (
                        <div className='px-1 py-1 bg-gray-50 border border-gray-200 rounded text-12 md:text-14 text-gray-700 leading-relaxed'>
                            This cart contains products sold under different Conditions of Sale. These terms may differ from other products available on Caroma Plumbers&apos; Hub.{' '}
                            <a
                                href="https://assets.contentstack.io/v3/assets/bltad292a6aa65b6a42/blt9d702106a2e319d5/6a4da51ec9cdbaabd7ac8bbe/Conditions_of_Sale_-_Leak_SmartShield.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className='underline !text-blue-600 hover:!text-blue-800'
                            >
                                <b>[Review Conditions of Sale]</b>.
                            </a>
                        </div>
                    )}
                    {locale === 'au' ? (
                        <div className='md:flex md:justify-end'>
                            <div>
                                <Button
                                    className={`w-full grow items-stretch justify-center whitespace-nowrap !bg-[#2B2C39] px-[1.5rem] py-3 text-center text-18 font-normal leading-6 text-white shadow-sm max-md:px-5 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={handlePayNow}
                                    variant='primary'
                                    disabled={isLoading}
                                >
                                    {"Proceed to Payment"}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className='md:flex md:justify-end'>
                            <div>
                                <Button
                                    className={`w-full grow items-stretch justify-center whitespace-nowrap !bg-[#2B2C39] px-[1.5rem] py-3 text-center text-18 font-normal leading-6 text-white shadow-sm max-md:px-5 cursor-pointer ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={handleWindcavePayment}
                                    variant='primary'
                                    disabled={isLoading}
                                >
                                    {"Proceed to Payment"}
                                </Button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
            <Popup isOpen={isModalOpen} bodyClassNames="sm:mx-5 sm:mx-unset max-w-[639px]">
                <div onClick={(e) => e.stopPropagation()} className="mb-[13px] flex justify-between pt-[24px] pr-[29px]">
                    <div>
                    </div>
                    <div className="cursor-pointer" onClick={closeModal}>
                        <Icon className="text-[#090909] w-[12px] h-[12px]" />
                    </div>
                </div>
                <div className="px-[52px] flex flex-col items-center pb-[60px]">
                    <Image src="/images/cph-logo.svg" className="w-[174px] h-[51px]" alt="Plumber's hub" />
                    <Typography className="text-[27px] text-black text-center font-semibold pb-[23px] pt-[62px]">Oops!</Typography>
                    <Typography className="text-[20px] text-black text-center max-w-[504px] font-normal pb-[56px]">{errorMessage}</Typography>
                    <div className="flex sm:flex-row flex-col items-center gap-6 md:gap-[44px] w-full">
                        {enableTryAgain && (
                            locale === 'au' ? (
                                <Button
                                    size="full"
                                    className="h-[60px] w-full !cursor-pointer !bg-black text-[20px] font-medium !shadow-lg"
                                    variant="primary"
                                    onClick={() => handlePayNow()}
                                >
                                    Try Again
                                </Button>
                            ) : (
                                <Button
                                    size="full"
                                    className="h-[60px] w-full !cursor-pointer !bg-black text-[20px] font-medium !shadow-lg"
                                    variant="primary"
                                    onClick={() => handleWindcavePayment()}
                                >
                                    Try Again
                                </Button>
                            )
                        )}
                        <Button
                            size="full"
                            className="h-[60px] w-full !cursor-pointer text-[20px] font-medium !shadow-lg"
                            variant="secondary"
                            onClick={() => router.push('/contact-us')}

                        >
                            Contact Us
                        </Button>
                    </div>
                </div>
            </Popup>
        </>
    )
};

export default ReviewOrderSummary;
